/** @jsxImportSource @emotion/react */

import type { CSSObject } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { CSSProperties } from "react";
import { cloneElement, useEffect, useRef } from "react";
import { Clay, Flex } from "../../utilities";
import { getValidChildren } from "../../utils";
import { tabListCSS } from "./TabList.css";
import { useTabs } from "./TabList.hooks";
import type { LegacyTabIndicatorStyle, LegacyTabListProps } from "./TabList.types";

const segmentTabIndicatorCSS = (type: LegacyTabListProps<string>["type"]): CSSObject => {
  switch (type) {
    case "raised":
      return {
        backgroundColor: vars.semantic.color.actionSecondary,
        boxShadow: vars.dropShadow.popover,
      };

    case "secondary":
      return {
        backgroundColor: vars.semantic.color.actionElevated,
      };

    case "outlined":
      return {
        border: `2px solid ${vars.semantic.color.actionPrimary}`,
        backgroundColor: "transparent",
      };

    // primary
    default:
      return {
        backgroundColor: vars.semantic.color.focus,
      };
  }
};

const renderTabIndicator = ({
  variant,
  tabIndicatorStyle,
  type,
  background,
}: Pick<LegacyTabListProps<string>, "variant" | "type" | "background"> & {
  tabIndicatorStyle?: LegacyTabIndicatorStyle;
}) => {
  const commonStyle: CSSProperties = {
    position: "absolute",
    left: tabIndicatorStyle?.left,
    width: tabIndicatorStyle?.width,
    height: tabIndicatorStyle?.height,
    transitionProperty: "left, width",
    transitionTimingFunction: "ease-out",
    ...(tabIndicatorStyle && {
      transitionDuration: "0.3s",
    }),
  };

  switch (variant) {
    case "segment":
      return (
        <Clay
          native={{
            "data-clay-tabs-indicator": true,
          }}
          {...commonStyle}
          bottom={background ? vars.spacing[1] : 0}
          backgroundColor={vars.semantic.color.focus}
          borderRadius={vars.rounded.small}
          sx={segmentTabIndicatorCSS(type)}
        />
      );

    case "underline":
      return (
        <Clay
          native={{
            "data-clay-tabs-indicator": true,
          }}
          {...commonStyle}
          bottom={0}
          height="2px"
          backgroundColor={vars.semantic.color.focus}
          borderRadius="50px"
        />
      );

    default:
      return null;
  }
};

/**
 * @deprecated Use `Tabs` instead.
 */
export const LegacyTabList = <T extends string>({
  children,
  variant,
  size = "medium",
  direction = "horizontal",
  background,
  fullWidth,
  type,
  activeValue,
  onChangeValue,
  sx,
}: LegacyTabListProps<T>) => {
  const { getTabProps, setValue, tabIndicatorStyle, value } = useTabs({
    defaultValue: activeValue ?? ("0" as T),
  });

  useEffect(() => {
    if (activeValue) setValue(activeValue);
  }, [activeValue, setValue]);

  const onChangeValueRef = useRef<LegacyTabListProps<T>["onChangeValue"]>();
  onChangeValueRef.current = onChangeValue;
  useEffect(() => {
    onChangeValueRef.current?.(value);
  }, [value]);

  const renderTabs = () => {
    const tabs = getValidChildren(children).map((child, index) => {
      const value = child.props.value ?? index.toString();

      return cloneElement(child, {
        ...getTabProps(value, index),
        variant,
      });
    });

    return tabs;
  };

  return (
    <Flex
      display={fullWidth ? "flex" : "inline-flex"}
      width={fullWidth ? "100%" : "fit-content"}
      position="relative"
      native={{
        role: "tablist",
      }}
      flexDirection={direction === "horizontal" ? "row" : "column"}
      css={[tabListCSS({ variant, size, background, type, fullWidth }), sx]}
    >
      {renderTabIndicator({ variant, tabIndicatorStyle, type, background })}

      {renderTabs()}
    </Flex>
  );
};
